import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api } from '../api/client';
import { useCartStore } from './cart';

export interface AuthUser {
  id: string;
  email: string;
  role?: string;
  firstName?: string | null;
  lastName?: string | null;
  [key: string]: unknown;
}

export interface LoginResult {
  twoFactorRequired: boolean;
  method?: 'TOTP' | 'EMAIL';
  challengeToken?: string;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const ready = ref(false);

  const isAuthenticated = computed(() => !!user.value);
  const displayName = computed(() => {
    const u = user.value;
    if (!u) return '';
    const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return full || u.email;
  });

  async function login(email: string, password: string, altcha = 'dev-bypass'): Promise<LoginResult> {
    // ALTCHA: real PoW payload from the widget in prod; "dev-bypass" is accepted
    // by the backend only outside production (keeps local dev frictionless).
    const { data } = await api.post('/auth/login', { email, password, altcha });
    if (data.twoFactorRequired) {
      // No session yet — caller must complete the second factor.
      return { twoFactorRequired: true, method: data.method, challengeToken: data.challengeToken };
    }
    user.value = data.user;
    return { twoFactorRequired: false };
  }

  // Second step of 2FA login — exchanges the challenge token + code for a session.
  async function verifyTwoFactor(challengeToken: string, code: string) {
    const { data } = await api.post('/auth/2fa/verify', { challengeToken, code });
    user.value = data.user;
  }

  async function forgotPassword(email: string, altcha = 'dev-bypass') {
    await api.post('/auth/forgot-password', { email, altcha });
  }

  async function verifyEmail(token: string) {
    await api.post('/auth/verify-email', { token });
    // Refresh session so emailVerified flips if already logged in.
    if (user.value) await fetchMe();
  }

  async function resendVerification() {
    await api.post('/auth/resend-verification');
  }

  async function register(email: string, password: string, altcha = 'dev-bypass') {
    const { data } = await api.post('/auth/register', { email, password, altcha });
    user.value = data.user;
  }

  let inflight: Promise<void> | null = null;
  async function fetchMe() {
    try {
      const { data } = await api.get('/auth/me');
      user.value = data.user;
    } catch {
      user.value = null;
    } finally {
      ready.value = true;
    }
  }

  // Resolve once the initial session check has completed (dedupes concurrent calls).
  async function ensureReady() {
    if (ready.value) return;
    if (!inflight) inflight = fetchMe().finally(() => { inflight = null; });
    await inflight;
  }

  async function logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      user.value = null;
      // Drop the cart so one account's items never carry over to the next
      // person using this browser (the cart lives in localStorage).
      useCartStore().clear();
    }
  }

  return { user, ready, isAuthenticated, displayName, login, verifyTwoFactor, register, forgotPassword, verifyEmail, resendVerification, fetchMe, ensureReady, logout };
});
